/**
 * FocalPointTopRail — cycle-102 sprint-1 Task 1.4.
 *
 * Top-N attention-priority entities + per-`source_group` (here: domain)
 * breakdown chips. Mounted in Workspace Zone 1 (top rail) per SDD §1.4.1.
 * TanStack staleTime 60s per sprint plan §244.
 */

import { useFocalPoints } from '../../../hooks/useIntel';
import type { FocalPoint } from '../../../api/intelClient';
import { cn } from '../../../lib/utils';

interface FocalPointTopRailProps {
  topN?: number;
  className?: string;
}

const URGENCY_COLOR: Record<string, string> = {
  CRITICAL: 'border-red-500/60 text-red-400',
  ELEVATED: 'border-amber-500/60 text-amber-400',
  ROUTINE: 'border-[var(--e-border-secondary)] text-[var(--e-text-secondary)]',
};

export function FocalPointTopRail({ topN = 10, className }: FocalPointTopRailProps) {
  const { data, isLoading, error } = useFocalPoints({ limit: topN });

  return (
    <div
      role="region"
      aria-label="Focal points top rail"
      className={cn(
        'flex items-center gap-2 px-3 h-9 border-b border-[var(--e-border-secondary)]/60 bg-[var(--e-bg-card)]/60 overflow-x-auto',
        className,
      )}
    >
      <span className="shrink-0 text-[9px] font-mono uppercase tracking-widest text-[var(--e-text-muted)]">
        Focal · Top {topN}
      </span>
      <div aria-hidden="true" className="shrink-0 h-3 w-px bg-[var(--e-border-secondary)]" />
      {isLoading && !data && (
        <span
          role="status"
          aria-live="polite"
          className="text-[10px] font-mono text-[var(--e-text-muted)]"
        >
          Loading focal points…
        </span>
      )}
      {error && !data && (
        <span
          role="alert"
          data-testid="focal-rail-error"
          className="text-[10px] font-mono text-amber-400"
        >
          Focal points unavailable
        </span>
      )}
      {data && data.focal_points.length === 0 && (
        <span
          role="status"
          className="text-[10px] font-mono text-[var(--e-text-muted)]"
        >
          No focal points in window
        </span>
      )}
      {data?.focal_points.map((fp) => (
        <FocalChip key={fp.h3_cell} fp={fp} />
      ))}
    </div>
  );
}

function FocalChip({ fp }: { fp: FocalPoint }) {
  const colour = URGENCY_COLOR[fp.urgency] ?? URGENCY_COLOR.ROUTINE;
  return (
    <button
      type="button"
      data-testid={`focal-chip-${fp.h3_cell}`}
      title={fp.suggested_action}
      className={cn(
        'shrink-0 inline-flex items-center gap-1.5 px-2 py-1 rounded-md border bg-[var(--e-bg-elevated)]/40',
        'text-[10px] font-mono uppercase tracking-wider',
        'hover:bg-[var(--e-bg-elevated)]',
        'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--e-purple-400)]',
        colour,
      )}
    >
      <span className="text-[var(--e-text-primary)]">{fp.geo_label}</span>
      <span className="text-[var(--e-text-muted)]">·</span>
      <span>{fp.focal_score.toFixed(2)}</span>
      <span className="text-[var(--e-text-muted)]">·</span>
      <span className="flex items-center gap-0.5">
        {fp.domains.slice(0, 4).map((domain) => (
          <span
            key={domain}
            className="px-1 rounded-sm bg-[var(--e-bg-card)]/80 text-[8px] text-[var(--e-text-muted)]"
          >
            {domain}
          </span>
        ))}
        {fp.domains.length > 4 && (
          <span className="text-[8px] text-[var(--e-text-muted)]">
            +{fp.domains.length - 4}
          </span>
        )}
      </span>
    </button>
  );
}
